/**
 * Turn a stored address into lines a human would write — BUILD-PLAN §43.
 *
 * ## Why this exists
 *
 * The confirmation page and the checkout review each built their own list by
 * putting every field on its own line, in schema order. That reads fine with
 * tidy data and falls apart with real data:
 *
 *     Rich Hope
 *     98 King St, Manchester M2 4WU, UK   ← browser autofill, whole address
 *     Manchester                          ← city, already above
 *     Manchester m2                       ← region, echoes the city
 *     M2 4WU                              ← postcode, already above
 *     GB                                  ← a code, not a country
 *
 * Nothing there is a data bug. Browser autofill legitimately drops a full
 * address into line 1, and administrative regions legitimately repeat the
 * city name. Rendering the schema instead of an address is what makes it look
 * broken.
 *
 * Two rules fix it:
 *
 *   1. **Locality on one line.** City, region and postcode belong together —
 *      "Manchester, Greater Manchester M2 4WU" — which is how the line is
 *      written on an envelope and how the eye expects to scan it.
 *   2. **Never repeat what is already shown.** Any part already present in an
 *      earlier line is dropped, compared loosely enough to catch differences
 *      of case, spacing and punctuation ("M2 4WU" vs "m2 4wu").
 *
 * The country is resolved to its NAME, because "GB" is a database value and
 * the customer is checking their own address, not reading our schema.
 */

export interface AddressLike {
  first_name?: string | null;
  last_name?: string | null;
  company?: string | null;
  line_1?: string | null;
  line_2?: string | null;
  city?: string | null;
  region?: string | null;
  postcode?: string | null;
  country_code?: string | null;
}

/** Loose enough to catch case, spacing and punctuation differences. */
function normalise(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

export function formatAddressLines(
  address: AddressLike,
  /** `code => name`, so "GB" is shown as "United Kingdom". */
  countryName?: (code: string) => string | undefined,
): string[] {
  const lines: string[] = [];
  const seen: string[] = [];

  /**
   * Add a line unless it is already covered by one above it.
   *
   * The containment test runs both ways: a short part inside a long line
   * (postcode inside an autofilled line 1) and a long line already carrying
   * a short one.
   */
  const push = (value: string | null | undefined) => {
    const text = value?.trim();

    if (!text) return;

    const key = normalise(text);

    if (key === '') return;
    if (seen.some((existing) => existing.includes(key) || key.includes(existing))) return;

    lines.push(text);
    seen.push(key);
  };

  push([address.first_name, address.last_name].filter(Boolean).join(' '));
  push(address.company);
  push(address.line_1);
  push(address.line_2);

  /*
   | The locality line, assembled before it is offered.
   |
   | Region is dropped when it merely repeats the city — "Manchester" /
   | "Manchester m2" is one place, not two, and printing both looks like a
   | mistake the customer made.
   */
  const city = address.city?.trim() ?? '';
  const region = address.region?.trim() ?? '';
  const postcode = address.postcode?.trim() ?? '';

  /*
   | Is the region worth showing next to the city?
   |
   | "contains the city" is too blunt a test: "Greater London" contains
   | "London" and is a real administrative region that belongs on the label,
   | while "Manchester m2" is the city with a fragment of postcode stuck to
   | it. The difference is what is LEFT once the city is removed — a
   | meaningful qualifier ("Greater", "West"), or noise containing digits.
   */
  const regionIsDistinct = (() => {
    if (region === '') return false;
    if (city === '') return true;

    const regionKey = normalise(region);
    const cityKey = normalise(city);

    if (regionKey === cityKey) return false;
    if (!regionKey.includes(cityKey)) return true;

    const remainder = regionKey.replace(cityKey, '');

    // Nothing left, or only digits/short noise — it is the city restated.
    return remainder.length > 2 && !/\d/.test(remainder);
  })();

  const locality = [
    [city, regionIsDistinct ? region : ''].filter(Boolean).join(', '),
    postcode,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  push(locality);

  const code = address.country_code?.trim();

  if (code) {
    // The name when we can resolve it; the code is a last resort, never a
    // preference.
    push(countryName?.(code) ?? code);
  }

  return lines;
}
